import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { Newspaper, Mic, BookOpen, Handshake, Linkedin, ExternalLink, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

function ContactForm({ type, fields, icon: Icon, title, subtitle }) {
  const [form, setForm] = useState({});
  const [status, setStatus] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { setStatus("Please enter your name."); return; }
    try {
      await api.post("/contact", { inquiry_type: type, name: form.name || "", email: form.email || "", organization: form.organization || "", message: form.message || "", extra_fields: form });
      setStatus("Inquiry submitted! Expect a response within 5\u20137 business days.");
      setForm({});
      setTimeout(() => setStatus(""), 6000);
    } catch { setStatus("Error. Try again."); }
  };

  const renderField = (f) => {
    if (f.type === "textarea") {
      return (
        <Textarea
          value={form[f.name] || ""}
          onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
          data-testid={`contact-${type}-${f.name}`}
          className="rounded-sm border-[#1B2A4A]/20 min-h-[100px]"
          placeholder={f.placeholder || ""}
        />
      );
    }
    
    if (f.type === "select") {
      return (
        <Select onValueChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))}>
          <SelectTrigger data-testid={`contact-${type}-${f.name}`} className="rounded-sm border-[#1B2A4A]/20">
            <SelectValue placeholder={f.placeholder || "Select..."} />
          </SelectTrigger>
          <SelectContent>
            {f.options.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
          </SelectContent>
        </Select>
      );
    }
    
    return (
      <Input
        type={f.type || "text"}
        value={form[f.name] || ""}
        onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
        data-testid={`contact-${type}-${f.name}`}
        className="rounded-sm border-[#1B2A4A]/20"
        placeholder={f.placeholder || ""}
      />
    );
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp}>
      <div className="flex items-center gap-3 mb-6">
        <Icon className="h-6 w-6 text-[#C9A84C]" />
        <div>
          <h3 className="font-heading font-semibold text-lg text-[#1B2A4A]">{title}</h3>
          <p className="text-sm text-[#1B2A4A]/60">{subtitle}</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {fields.map((f) => (
          <div key={f.name}>
            <Label className="text-[#1B2A4A] text-sm mb-1.5 block">{f.label}{f.required && " *"}</Label>
            {renderField(f)}
          </div>
        ))}
        <Button type="submit" data-testid={`contact-${type}-submit`} className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm px-8">
          Submit Inquiry
        </Button>
        {status && <p className="text-sm text-[#1B2A4A] font-medium">{status}</p>}
      </form>
    </motion.div>
  );
}

const MEDIA_FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone Number", type: "tel" },
  { name: "preferred_contact", label: "Preferred Contact Method", type: "select", options: ["Email", "Phone", "Either"], placeholder: "Select preference" },
  { name: "best_time", label: "Best Time to Reach You", type: "select", options: ["Morning (9am-12pm)", "Afternoon (12pm-5pm)", "Evening (5pm-8pm)", "Anytime"], placeholder: "Select time" },
  { name: "organization", label: "Outlet / Publication" },
  { name: "format", label: "Format", type: "select", options: ["Interview", "Podcast", "Editorial", "Other"], placeholder: "Select format" },
  { name: "deadline", label: "Deadline" },
  { name: "message", label: "Message", type: "textarea" },
];

const PUBLISHING_FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone Number", type: "tel" },
  { name: "preferred_contact", label: "Preferred Contact Method", type: "select", options: ["Email", "Phone", "Either"], placeholder: "Select preference" },
  { name: "best_time", label: "Best Time to Reach You", type: "select", options: ["Morning (9am-12pm)", "Afternoon (12pm-5pm)", "Evening (5pm-8pm)", "Anytime"], placeholder: "Select time" },
  { name: "organization", label: "Organization" },
  { name: "inquiry_subtype", label: "Inquiry Type", type: "select", options: ["Bulk Orders", "Licensing", "Review Copies", "Ghostwriting", "Other"], placeholder: "Select type" },
  { name: "message", label: "Message", type: "textarea" },
];

const PARTNERSHIP_FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone Number", type: "tel" },
  { name: "preferred_contact", label: "Preferred Contact Method", type: "select", options: ["Email", "Phone", "Either"], placeholder: "Select preference" },
  { name: "best_time", label: "Best Time to Reach You", type: "select", options: ["Morning (9am-12pm)", "Afternoon (12pm-5pm)", "Evening (5pm-8pm)", "Anytime"], placeholder: "Select time" },
  { name: "organization", label: "Organization" },
  { name: "inquiry_subtype", label: "Inquiry Type", type: "select", options: ["Reform Intelligence Engagement", "VALOR Partnership", "MOBC Inquiry", "Other"], placeholder: "Select type" },
  { name: "message", label: "Message", type: "textarea" },
];

export default function Contact() {
  return (
    <div>
      {/* HERO */}
      <SEO
        title="Contact"
        description="Connect with Dr. Ben Magee — media inquiries, speaking engagements, publishing and bulk orders, partnerships and consulting. Choose the right channel."
        keywords="contact Dr. Ben Magee, speaking engagement booking, media inquiries, Reform Intelligence consulting, VALOR partnership"
        page="contact"
        canonical="https://drbenmagee.com/contact"
      />
      <section className="bg-[#1B2A4A] py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="w-1 h-12 bg-[#C9A84C] mb-8" />
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" data-testid="contact-hero-title">
            Let's Connect. Choose the Right Channel.
          </h1>
        </div>
      </section>

      {/* FORMS */}
      <section className="py-20 md:py-32 bg-white" data-testid="contact-forms">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <Tabs defaultValue="media" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 bg-[#E8EDF5] rounded-sm h-auto p-1 mb-10">
              <TabsTrigger value="media" data-testid="contact-tab-media" className="rounded-sm text-xs sm:text-sm py-2.5 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <Newspaper className="h-4 w-4 mr-1.5 hidden sm:inline" /> Media
              </TabsTrigger>
              <TabsTrigger value="speaking" data-testid="contact-tab-speaking" className="rounded-sm text-xs sm:text-sm py-2.5 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <Mic className="h-4 w-4 mr-1.5 hidden sm:inline" /> Speaking
              </TabsTrigger>
              <TabsTrigger value="publishing" data-testid="contact-tab-publishing" className="rounded-sm text-xs sm:text-sm py-2.5 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <BookOpen className="h-4 w-4 mr-1.5 hidden sm:inline" /> Publishing
              </TabsTrigger>
              <TabsTrigger value="partnerships" data-testid="contact-tab-partnerships" className="rounded-sm text-xs sm:text-sm py-2.5 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <Handshake className="h-4 w-4 mr-1.5 hidden sm:inline" /> Partnerships
              </TabsTrigger>
            </TabsList>

            <TabsContent value="media">
              <ContactForm
                type="media"
                icon={Newspaper}
                title="Media & Press"
                subtitle="Interview requests, editorial inquiries, podcast appearances"
                fields={MEDIA_FIELDS}
              />
            </TabsContent>

            <TabsContent value="speaking">
              <div className="text-center py-12">
                <Mic className="h-12 w-12 text-[#C9A84C] mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-xl text-[#1B2A4A] mb-2">Speaking Engagement</h3>
                <p className="text-sm text-[#1B2A4A]/60 mb-6">For conference, corporate, and event bookings</p>
                <a href="/speaking#booking">
                  <Button data-testid="contact-speaking-redirect" className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm px-8">
                    Go to Speaking Booking Form
                  </Button>
                </a>
              </div>
            </TabsContent>

            <TabsContent value="publishing">
              <ContactForm
                type="publishing"
                icon={BookOpen}
                title="Publishing & Books"
                subtitle="Bulk orders, licensing, review copies, ghostwriting inquiries"
                fields={PUBLISHING_FIELDS}
              />
            </TabsContent>

            <TabsContent value="partnerships">
              <ContactForm
                type="partnerships"
                icon={Handshake}
                title="Partnerships & Consulting"
                subtitle="Reform Intelligence engagements, VALOR partnerships, MOBC inquiries"
                fields={PARTNERSHIP_FIELDS}
              />
            </TabsContent>
          </Tabs>

          {/* RESPONSE NOTE */}
          <div className="mt-12 p-6 bg-[#F5EDD6] rounded-sm border border-[#C9A84C]/20">
            <p className="text-sm text-[#1B2A4A]/70">
              <span className="font-semibold text-[#1B2A4A]">Response time:</span> Dr. Magee personally reviews all inquiries. Expect a response within 5&ndash;7 business days.
            </p>
          </div>

          {/* SOCIAL LINKS */}
          <div className="mt-8 flex items-center gap-6" data-testid="contact-social-links">
            <a href="https://linkedin.com/in/drbenmagee" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#1B2A4A]/60 hover:text-[#C9A84C] transition-colors">
              <Linkedin className="h-5 w-5" /> LinkedIn
            </a>
            <a href="https://www.amazon.com/stores/Dr.-Ben-Magee/author/B0GKHHQQ1J" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#1B2A4A]/60 hover:text-[#C9A84C] transition-colors">
              <ShoppingBag className="h-5 w-5" /> Amazon Author Central
            </a>
            <a href="https://valorlinkusa.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#1B2A4A]/60 hover:text-[#C9A84C] transition-colors">
              <ExternalLink className="h-5 w-5" /> VALORLink&trade;
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
